/**
 * Class representing a Wishlist Modal.
 * Extends the ModalOpener class.
 */
class WishlistModal extends ModalOpener {
    /**
     * Constructor for WishlistModal.
     * Initializes the modal and sets up event listeners.
     */
    constructor() {
        super();

        // Select elements
        this.compareBar = document.querySelector('compare-bar');
        this.favoritesEl = this.querySelector('#favorites');
        this.noFavoritesEl = this.querySelector('.wishlist-modal__no-products');
        this.countEl = this.querySelector('[data-count]');
        this.countBubbleEl = document.querySelector('.wishlist-count-bubble');
        this.favoritesCount = 0;
        this.clearAllBtn = this.querySelector('[data-clear-all]');

        // If favorites element is not found, return early
        if (!this.favoritesEl) return;

        // If wishlistWorkerUrl variable is false, return early
        if (!p80.wishlistWorkerUrl) return;

        // Add event listener for clearing all favorites
        this.clearAllBtn.addEventListener('click', this.resetHandles.bind(this));
        this.initialize();
    }

    /**
     * Initialize the modal by fetching products.
     */
    initialize() {
        this.fetchProducts();
    }

    /**
     * Fetch products from the server and inject them into the modal.
     * Uses async/await for asynchronous operations.
     */
    async fetchProducts() {
        try {
            const response = await fetch('/pages/favorites');
            if (!response.ok) throw new Error('Network response was not ok');
            const html = await response.text();
            this.injectProducts(html);
        } catch (error) {
            console.error('Failed to fetch products:', error);
        }
    }

    /**
     * Update the count of favorite items.
     * @param {number|null} count - The new count of favorite items.
     */
    updateCount(count = null) {
        if (!this.countEl) return;
        this.countEl.textContent = count !== 0 ? this.favoritesCount : count;

        if (!this.countBubbleEl) return;
        if (count > 0 || this.favoritesCount > 0) {
            this.countBubbleEl.textContent = count !== 0 ? this.favoritesCount : count;
            this.countBubbleEl.classList.remove('hidden');
        } else {
            this.countBubbleEl.classList.add('hidden');
        }
    }

    /**
     * Inject products into the modal and update the count.
     * @param {string} html - The HTML string of products to inject.
     */
    injectProducts(html) {
        if (!html) return;

        // Inject HTML into the favorites element
        this.favoritesEl.innerHTML = html;
        const gridItems = this.favoritesEl.querySelectorAll('.grid__item');
        this.favoritesCount = gridItems.length;

        // Update the count and toggle visibility of the no favorites element
        this.updateCount();
        this.noFavoritesEl.classList.toggle('visible', this.favoritesCount === 0);

        // Initialize the compare bar if there are favorite items
        if (this.favoritesCount > 0 && this.compareBar) {
            this.compareBar.initialize();
        }
    }

    /**
     * Reset the wishlist handles and sync with the server.
     * Uses async/await for asynchronous operations.
     */
    async resetHandles() {
        p80.wishlist.handles = 'NONE';
        try {
            const response = await fetch(p80.wishlistWorkerUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(p80.wishlist)
            });

            if (!response.ok) throw new Error('Network response was not ok');

            const responseData = await response.json();
            console.log('Sync successful:', responseData);

            // Update the count and show the empty state
            this.favoritesCount = 0;
            this.updateCount(0);
            this.showEmpty();
            this.uncheckCardsWishlist();
        } catch (error) {
            console.error('Sync failed:', error);
        }
    }

    /**
     * Uncheck all wishlist buttons in the cards.
     */
    uncheckCardsWishlist() {
        document.querySelectorAll('wishlist-button.added').forEach((button) => {
            button.classList.remove('added');
        });
    }

    /**
     * Show the empty state when there are no favorite items.
     */
    showEmpty() {
        this.noFavoritesEl.classList.add('visible');
    }
}

// Define the custom element for the wishlist modal
customElements.define('wishlist-modal', WishlistModal);

/**
 * Class representing a Wishlist Button.
 * Extends the HTMLElement class.
 */
class WishlistButton extends HTMLElement {
    constructor() {
        super();
        this.handleClickBound = this.handleClick.bind(this);
    }

    connectedCallback() {
        this.wishlistModal = document.querySelector('wishlist-modal');
        this.isInModal = !!this.closest('.wishlist-modal__product-grid-item');

        // Check if p80.wishlist exists and p80.wishlist.customer_id is not null
        if (p80?.wishlist?.customer_id) {
            // Add a click event listener to the custom element itself
            this.addEventListener('click', this.handleClickBound);
        } else {
            // Redirect to /account/login if customer_id is null or doesn't exist
            this.addEventListener('click', () => {
                window.location.href = '/account/login';
            });
        }
    }

    disconnectedCallback() {
        // Remove the click event listener
        this.removeEventListener('click', this.handleClickBound);
    }

    handleClick(event) {
        const button = event.target;
        const newHandle = button.dataset.handle;
        let currentHandles = p80.wishlist.handles.split('|').filter((handle) => handle);

        // Disable the button and add loading class
        button.setAttribute('disabled', 'disabled');
        this.classList.add('adding');

        // Check if the handle exists in the currentHandles array
        const handleIndex = currentHandles.indexOf(newHandle);
        if (handleIndex > -1) {
            // If it exists, remove it
            currentHandles.splice(handleIndex, 1);
        } else {
            // If it doesn't exist, add it
            currentHandles.push(newHandle);
        }

        // Update the handles
        p80.wishlist.handles = currentHandles.length ? currentHandles.join('|') : 'NONE';

        // Sync the wishlist handles
        this.syncHandles(button);
    }

    /**
     * Sync the wishlist handles with the server.
     * Uses async/await for asynchronous operations.
     * @param {HTMLElement} button - The button element.
     */
    async syncHandles(button) {
        try {
            const response = await fetch(p80.wishlistWorkerUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(p80.wishlist)
            });

            if (!response.ok) throw new Error('Network response was not ok');

            const responseData = await response.json();
            console.log('Sync successful:', responseData);

            // Remove loading class and toggle button state
            button.classList.remove('adding');
            this.toggleButtonState(button);

            // Enable the button
            button.removeAttribute('disabled');

            // Update wishlist modal
            this.wishlistModal?.fetchProducts();
        } catch (error) {
            console.error('Sync failed:', error);
        }
    }

    /**
     * Toggle the state of the button.
     * @param {HTMLElement} button - The button element.
     */
    toggleButtonState(button) {
        const buttonHandle = button.dataset.handle;
        const sameWLButtons = document.querySelectorAll(
            `wishlist-button[data-handle="${buttonHandle}"]`
        );

        sameWLButtons.forEach((btn) => {
            btn.classList.toggle('added');
        });
    }
}

// Define the custom element for the wishlist button
customElements.define('wishlist-button', WishlistButton);
